/**
 * 视频字幕校验：每秒字数、时间顺序等
 * SRT 时间格式：HH:MM:SS,mmm 或 HH:MM:SS.mmm
 */

export const DEFAULT_MAX_CHARS_PER_SECOND = 26;

/**
 * SRT 时间字符串转秒数
 * @param {string} s - 如 "00:00:01,020"
 * @returns {number}
 */
export function srtTimeToSeconds(s) {
  if (!s || typeof s !== "string") return 0;
  const normalized = s.trim().replace(",", ".");
  const parts = normalized.split(":");
  if (parts.length !== 3) return 0;
  const h = parseInt(parts[0], 10) || 0;
  const m = parseInt(parts[1], 10) || 0;
  const secParts = parts[2].split(".");
  const sec = parseInt(secParts[0], 10) || 0;
  const ms = parseInt(secParts[1] || "0", 10) || 0;
  return h * 3600 + m * 60 + sec + ms / 1000;
}

/**
 * 秒数转 SRT 时间字符串
 * @param {number} seconds
 * @returns {string}
 */
export function secondsToSrtTime(seconds) {
  const s = Number(seconds);
  if (isNaN(s) || s < 0) return "00:00:00,000";
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = Math.floor(s % 60);
  const ms = Math.round((s % 1) * 1000);
  return [
    String(h).padStart(2, "0"),
    String(m).padStart(2, "0"),
    String(sec).padStart(2, "0") + "," + String(ms).padStart(3, "0")
  ].join(":");
}

export function getDefaultMaxCharsPerSecond() {
  return DEFAULT_MAX_CHARS_PER_SECOND;
}

/**
 * 校验单条字幕的每秒字数
 * @param {object} item - { text, startSeconds, endSeconds }
 * @param {number} maxCharsPerSecond
 * @returns {{ type: string, index: number, message: string } | null}
 */
export function validateCharsPerSecond(item, maxCharsPerSecond) {
  const duration = item.endSeconds - item.startSeconds;
  if (duration <= 0) return { type: "timeOrder", index: item.index, messageKey: "subtitle.endAfterStart" };
  const cps = item.text.length / duration;
  if (cps > maxCharsPerSecond) {
    return {
      type: "charsPerSecond",
      index: item.index,
      messageKey: "subtitle.charsPerSecondExceeded",
      messageParams: { cps: cps.toFixed(1), max: maxCharsPerSecond }
    };
  }
  return null;
}

/**
 * 校验当前条开始时间 >= 上一条结束时间
 * @param {object} item
 * @param {object} prev - 上一条
 * @returns {{ type: string, index: number, message: string } | null}
 */
export function validateTimeOrder(item, prev) {
  if (!prev) return null;
  if (item.startSeconds < prev.endSeconds) {
    return {
      type: "timeOrder",
      index: item.index,
      messageKey: "subtitle.startAfterPrevEnd",
      messageParams: { prevEndTime: prev.endTime }
    };
  }
  return null;
}

/**
 * 校验 start < end
 */
export function validateStartEnd(item) {
  if (item.startSeconds >= item.endSeconds) {
    return { type: "timeOrder", index: item.index, messageKey: "subtitle.startBeforeEnd" };
  }
  return null;
}

/**
 * 全量校验，返回错误列表
 * @param {Array} srtList - 按 index 排序的字幕列表
 * @param {number} maxCharsPerSecond
 * @returns {Array<{ type: string, index: number, message: string }>}
 */
export function validateSrtList(srtList, maxCharsPerSecond = DEFAULT_MAX_CHARS_PER_SECOND) {
  const errors = [];
  const list = Array.isArray(srtList) ? srtList : [];
  for (let i = 0; i < list.length; i++) {
    const item = list[i];
    const prev = list[i - 1] || null;
    const e1 = validateStartEnd(item);
    if (e1) errors.push(e1);
    const e2 = validateTimeOrder(item, prev);
    if (e2) errors.push(e2);
    const e3 = validateCharsPerSecond(item, maxCharsPerSecond);
    if (e3) errors.push(e3);
  }
  return errors;
}

/**
 * 将字幕数组转换为 SRT 格式字符串
 * 每条格式：序号 \n 开始时间 --> 结束时间 \n 字幕内容 \n
 * @param {Array} srcSrtContent - [{ index, startTime, endTime, text, translatedText }, ...]
 * @returns {string}
 */
export function srtListToSrtString(srcSrtContent) {
  if (!Array.isArray(srcSrtContent) || srcSrtContent.length === 0) return "";
  const lines = [];
  for (const item of srcSrtContent) {
    lines.push(String(item.index));
    const start = (item.startTime || secondsToSrtTime(item.startSeconds || 0)).trim();
    const end = (item.endTime || secondsToSrtTime(item.endSeconds || 0)).trim();
    lines.push(`${start} --> ${end}`);
    const text = (item.translatedText != null && item.translatedText !== "")
      ? item.translatedText
      : (item.text != null ? item.text : "");
    lines.push(text);
    lines.push("");
  }
  return lines.join("\n").replace(/\n+$/, "");
}
